class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[name="q"]');
    this.predictiveSearchResults = this.querySelector(
      '[data-ref="predictive-search"]'
    );

    /* 🟨 ===== DÉBUT AJOUT MSRP : CONTEXTE ET FILTRE ===== */

    this.searchFilter = this.dataset.searchFilter || '';
    this.searchMode = this.dataset.searchMode || '';
    this.isMsrpSearch = this.closest('.home-search-native') !== null;

    /* 🟨 ===== FIN AJOUT MSRP ===== */

    this.input?.addEventListener('focus', () => {
      for (const path of this.getSearchCatalogPaths()) this.loadCollectionSearchProducts(path).catch(() => {});
    });

    this.querySelector('form')?.addEventListener('submit', event => {
      if (this.isMsrpSearch || this.searchFilter) {
        event.preventDefault();
        this.onChange();
      }
    });

    this.requestId = 0;
    this.abortController = null;
    const delayedSearch = this.debounce(() => this.onChange(), 300);

    this.input?.addEventListener('input', () => {
      this.requestId += 1;
      this.abortController?.abort();
      if (this.input.value.replace(/\s/g, '').length < this.minimumSearchLength()) this.hide();
      else if (this.isMsrpSearch || this.searchFilter) this.showSearchStatus(this.searchText('loading'));
      delayedSearch();
    });
  }


  /* Début modification MSRP - recherche partielle dans la collection, sans fichier Liquid supplémentaire. */
  static collectionSearchCache = new Map();
  static productSearchCache = new Map();

  getCollectionSearchPath() {
    if (this.dataset.searchCollection) return this.dataset.searchCollection;
    const tag = this.getSearchFilterTag();
    if (tag === 'voiture' || tag === 'moto') return `${window.Shopify?.routes?.root || '/'}collections/stylo-retouche-peinture-${tag}`;
    if (!this.getSearchFilterBrand()) return '';

    const match = window.location.pathname.match(
      /^(.*\/collections\/[^/]+)\/?$/
    );

    return match ? match[1] : '';
  }

  async fetchSearchJson(url) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(url, {
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(
          `Recherche MSRP : HTTP ${response.status}`
        );
      }

      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  loadSearchProduct(handle) {
    const root =
      window.Shopify?.routes?.root || '/';

    const url =
      `${root}products/${encodeURIComponent(handle)}.js`;

    const cache =
      PredictiveSearch.productSearchCache;

    const existing = cache.get(url);

    if (
      existing &&
      Date.now() - existing.created < 60000
    ) {
      return existing.promise;
    }

    const entry = {
      created: Date.now(),

      promise: this.fetchSearchJson(url).catch(
        (error) => {
          if (cache.get(url) === entry) {
            cache.delete(url);
          }

          throw error;
        }
      )
    };

    if (cache.size >= 100) {
      cache.delete(cache.keys().next().value);
    }

    cache.set(url, entry);

    return entry.promise;
  }

  minimumSearchLength() {
    return this.getSearchFilterBrand().toLowerCase() !== 'ral' && (this.isMsrpSearch || this.searchFilter) ? 1 : 2;
  }

  searchText(key, values = {}) {
    const node = document.querySelector('[data-msrp-search-messages]');
    let messages = {};
    try { messages = JSON.parse(node?.textContent || '{}'); } catch {}
    let value = messages[key] || key;
    for (const [name, replacement] of Object.entries(values)) value = value.replaceAll(`%${name}%`, String(replacement));
    return value;
  }

  rawProductCode(product) {
    const prefix = this.normalizeText(product.title.split('&')[0]).trim();
    const type = this.normalizeText(product.productType || product.type || '').trim();
    const pattern = type.split(/[\s-]+/).map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('[\\s-]*');
    return prefix.replace(new RegExp(`^${pattern}\\s*`, 'i'), '').trim();
  }

  rankSearchProducts(catalog, prepared) {
    const brand = this.normalizeText(this.getSearchFilterBrand()).trim();
    const tag = this.normalizeText(this.getSearchFilterTag()).trim();
    const code = prepared.searchedCode;
    const raw = this.normalizeText(prepared.term).replace(/\s/g, '');
    const words = this.normalizeText(prepared.term).split(/\s+/).filter(Boolean);
    const ral = brand === 'ral';
    const matches = [];
    for (const product of catalog) {
      if (brand && this.normalizeText(product.productType || product.type || '').trim() !== brand) continue;
      if (tag && !(product.tags || []).some(t => this.normalizeText(t) === tag)) continue;
      const rawCode = this.rawProductCode(product);
      const compact = this.normalizeCode(rawCode);
      let rank = Infinity;
      if (ral && (!/^\d{4}$/.test(compact) || !product.handle.startsWith(`ral-${compact}-`))) continue;
      if (ral && code && !compact.startsWith(code)) continue;
      if (code && rawCode.replace(/\s/g, '') === raw) rank = 0;
      else if (code && compact === code) rank = 1;
      else if (code && compact.startsWith(code)) rank = 2;
      else if (code && /\d/.test(code) && /^[a-z]/.test(compact) && compact.slice(1) === code) rank = 3;
      else if (code && code.length >= 4 && /\d/.test(code) && /^l/.test(code) && compact === code.slice(1) && !(product.productType || '').toLowerCase().startsWith('moto ')) rank = 3;
      else if (code && compact.includes(code)) rank = 4;
      else if ((!code || !/\d/.test(code)) && words.every(word => this.normalizeText(product.title).includes(word))) rank = 5;
      if (Number.isFinite(rank)) matches.push({product, rank});
    }
    return matches.sort((a,b) => a.rank-b.rank || a.product.title.localeCompare(b.product.title, 'fr', {numeric:true, sensitivity:'base'}));
  }

  async getCollectionSearchResults(path, prepared) {
    const catalog = await this.loadCollectionSearchProducts(path);
    return this.renderSearchMatches(this.rankSearchProducts(catalog, prepared));
  }

  serverSearchQuery(prepared) {
    const scope = this.searchFilter;
    const clean = this.normalizeText(prepared.term).replace(/[^a-z0-9\s/._-]/g, '').trim();
    const variants = new Set();
    if (prepared.searchedCode) {
      const code = prepared.searchedCode;
      variants.add(clean.replace(/\s/g, ''));
      variants.add(code);
      if (code.length >= 2 && code.length <= 16) {
        for (let i = 1; i < code.length; i++) variants.add(`${code.slice(0,i)}/${code.slice(i)}`);
        if (/\d/.test(code)) {
          for (const letter of 'abcdefghijklmnopqrstuvwxyz') variants.add(letter + code);
          if (code.length >= 4 && /^l/.test(code)) variants.add(code.slice(1));
        }
      }
      const terms = [...variants].filter(Boolean).map(v => `title:${v}*`);
      return `${scope ? `(${scope}) AND ` : ''}(${terms.join(' OR ')})`;
    }
    const terms = clean.split(/\s+/).filter(Boolean).map(v => `title:${v}*`);
    return `${scope ? `(${scope}) AND ` : ''}(${terms.join(' AND ')})`;
  }

  static scopedSearchCache = new Map();

  async loadScopedSearchProducts(prepared, signal) {
    const query = this.serverSearchQuery(prepared);
    const cacheKey = `${document.documentElement.lang}|${window.Shopify?.currency?.active}|${query}|complete`;
    const cached = PredictiveSearch.scopedSearchCache.get(cacheKey);
    if (cached && Date.now() - cached.created < 60000) return cached.products;
    const products = new Map();
    const visited = new Set();
    let page = 1;
    while (true) {
      const url = new URL(`${window.Shopify?.routes?.root || '/'}search`, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      url.searchParams.set('options[unavailable_products]', 'last');
      url.searchParams.set('section_id', 'msrp-search-results');
      url.searchParams.set('page', String(page));
      const response = await fetch(url, {signal});
      if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = doc.querySelector('[data-msrp-search-page]');
      if (!node) throw new Error('Search response missing');
      const data = JSON.parse(node.textContent);
      if (!Array.isArray(data.products) || data.page !== page || visited.has(data.page)) throw new Error('Search pagination invalid');
      visited.add(data.page);
      for (const product of data.products) products.set(product.handle, product);
      if (page >= data.pages) break;
      if (!data.products.length || page >= 100) throw new Error('Search too broad');
      page++;
    }
    const result = [...products.values()];
    if (PredictiveSearch.scopedSearchCache.size >= 30) PredictiveSearch.scopedSearchCache.delete(PredictiveSearch.scopedSearchCache.keys().next().value);
    PredictiveSearch.scopedSearchCache.set(cacheKey, {products:result, created:Date.now()});
    return result;
  }

  async renderSearchMatches(matches) {
    const load = async start => Promise.all(matches.slice(start, start + 10).map(({product}) =>
      typeof product.price === 'number' ? product : this.loadSearchProduct(product.handle)));
    const section = this.renderSearchProducts(await load(0));
    if (!matches.length) return section;
    let shown = Math.min(10, matches.length);
    const footer = document.createElement('div');
    footer.className = 'msrp-search-pagination';
    footer.style.cssText = 'grid-column:1/-1;clear:both;padding:12px;text-align:center;background:#fff;color:#17212e;border-radius:8px;margin-top:10px;';
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.style.cssText = 'margin:0 0 8px;font-size:13px;line-height:1.4;';
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'button button--primary';
    more.style.cssText = 'background:#0b63ce;color:white;padding:10px 16px;border:0;border-radius:8px;cursor:pointer;';
    const update = () => {
      status.textContent = this.searchText('count', {shown,total:matches.length});
      more.textContent = this.searchText('more');
      more.hidden = shown >= matches.length;
      more.style.display = more.hidden ? 'none' : '';
    };
    more.addEventListener('click', async event => {
      event.preventDefault();
      more.disabled = true;
      try {
        const extra = this.renderSearchProducts(await load(shown));
        section.querySelector('.predictive-search__grid').append(...extra.querySelectorAll('.predictive-search__item-product'));
        shown = Math.min(shown + 10, matches.length);
        update();
      } catch {
        status.textContent = this.searchText('error');
      } finally { more.disabled = false; }
    });
    footer.append(status,more);
    section.append(footer);
    update();
    return section;
  }

  getSearchCatalogPaths() {
    const path = this.getCollectionSearchPath();
    if (path) return [path];
    if (!this.isMsrpSearch || this.searchFilter) return [];
    const root = window.Shopify?.routes?.root || '/';
    return ['voiture', 'moto', 'ral'].map(type => `${root}collections/stylo-retouche-peinture-${type}`);
  }

  async getScopedSearchResults(searchTerm) {
    const prepared = this.prepareSearchTerm(searchTerm);
    const id = ++this.requestId;
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    const timeout = setTimeout(() => controller.abort(), 20000);
    const current = () => id === this.requestId && this.input.value.trim() === searchTerm;
    this.showSearchStatus(this.searchText('loading'));
    try {
      const paths = this.getSearchCatalogPaths();
      const catalogs = paths.length
        ? await Promise.all(paths.map(path => this.loadCollectionSearchProducts(path)))
        : [await this.loadScopedSearchProducts(prepared, controller.signal)];
      if (!current()) return;
      const matches = this.rankSearchProducts(catalogs.flat(), prepared);
      const section = await this.renderSearchMatches(matches);
      if (!current()) return;
      if (!section.querySelector('.predictive-search__item-product')) this.showNoResults();
      else {
        this.predictiveSearchResults.replaceChildren(section);
        this.show();
      }
    } catch (error) {
      if (current()) this.showSearchStatus(this.searchText('error'));
    } finally { clearTimeout(timeout); }
  }

  loadCollectionSearchProducts(path) {
    const key = `msrp-search-index-v1:${document.documentElement.lang}:${path}`;
    const cache = PredictiveSearch.collectionSearchCache;
    const existing = cache.get(key);
    if (existing && Date.now() - existing.created < 300000) return existing.promise;
    const decode = rows => rows.map(([title,handle,productType,tags]) => ({title,handle,productType,tags}));
    try {
      const stored = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (stored && Date.now() - stored.created < 300000) {
        const promise = Promise.resolve(decode(stored.rows));
        cache.set(key,{promise,created:stored.created,ready:true});
        return promise;
      }
    } catch {}
    const entry = {created:Date.now(),promise:null};
    entry.promise = (async () => {
      const read = async page => {
        const url = new URL(path, location.origin);
        url.searchParams.set('section_id','msrp-search-results');
        url.searchParams.set('page',String(page));
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(),15000);
        try {
          const response = await fetch(url,{signal:controller.signal});
          if (!response.ok) throw new Error(`Catalogue HTTP ${response.status}`);
          const doc = new DOMParser().parseFromString(await response.text(),'text/html');
          const node = doc.querySelector('[data-msrp-search-page]');
          if (!node) throw new Error('Catalogue response missing');
          const data = JSON.parse(node.textContent);
          if (!Array.isArray(data.rows) || data.page !== page) throw new Error('Catalogue pagination invalid');
          return data;
        } finally {clearTimeout(timeout);}
      };
      const first = await read(1);
      const rows = [...first.rows];
      let nextPage = 2;
      const worker = async () => {
        while (nextPage <= first.pages) {
          const page = nextPage++;
          const result = await read(page);
          rows.push(...result.rows);
        }
      };
      await Promise.all(Array.from({length: Math.min(12, Math.max(0, first.pages - 1))}, worker));
      const unique = [...new Map(rows.map(row=>[row[1],row])).values()];
      entry.ready = true;
      try {sessionStorage.setItem(key,JSON.stringify({created:Date.now(),rows:unique}));} catch {}
      return decode(unique);
    })().catch(error=>{if(cache.get(key)===entry)cache.delete(key);throw error;});
    cache.set(key,entry);
    return entry.promise;
  }

  renderSearchProducts(products) {
    const section =
      document.createElement('div');

    section.className =
      'predictive-search-results';

    if (!products.length) {
      return section;
    }

    const group =
      document.createElement('div');

    group.className =
      'predictive-search__section';

    const heading =
      document.createElement('h3');

    heading.className =
      'h6 predictive-search__title';

    heading.textContent = 'Produits';

    const list =
      document.createElement('ul');

    list.className =
      'predictive-search__grid';

    const currency =
      window.Shopify?.currency?.active ||
      'EUR';

    const format =
      new Intl.NumberFormat(
        document.documentElement.lang ||
          'fr',
        {
          style: 'currency',
          currency
        }
      );

    const root =
      window.Shopify?.routes?.root ||
      '/';

    for (const product of products) {
      const item =
        document.createElement('li');

      item.className =
        'predictive-search__item-product';

      const link =
        document.createElement('a');

      link.href =
        `${root}products/${encodeURIComponent(
          product.handle
        )}`;

      if (product.featured_image) {
        const img =
          document.createElement('img');

        const imageUrl =
          new URL(
            product.featured_image,
            window.location.origin
          );

        imageUrl.searchParams.set(
          'width',
          '480'
        );

        img.src = imageUrl.href;
        img.alt = product.title;
        img.width = 480;
        img.height = 480;
        img.loading = 'lazy';

        link.appendChild(img);
      }

      const title =
        document.createElement('p');

      title.textContent =
        product.title;

      const price =
        document.createElement('span');

      price.className = 'price';

      price.textContent =
        (
          product.price_varies
            ? 'À partir de '
            : ''
        ) +
        format.format(
          product.price / 100
        );

      /* Début modification MSRP - conserver les classes de prix du thème. */

      price.setAttribute(
        'data-ref',
        'price'
      );

      price.setAttribute(
        'data-value',
        String(product.price)
      );

      const priceContainer =
        document.createElement('div');

      priceContainer.className =
        'product-price-container';

      const innerPrice =
        document.createElement('div');

      innerPrice.className =
        'price-container';

      const prices =
        document.createElement('div');

      prices.className = 'prices';

      const compare =
        document.createElement('span');

      compare.className =
        'compare-at-price';

      compare.setAttribute(
        'data-ref',
        'compare-at-price'
      );

      if (
        product.compare_at_price >
        product.price
      ) {
        compare.textContent =
          format.format(
            product.compare_at_price /
              100
          );

        compare.setAttribute(
          'data-value',
          String(
            product.compare_at_price
          )
        );
      }

      prices.append(
        price,
        compare
      );

      innerPrice.appendChild(
        prices
      );

      priceContainer.appendChild(
        innerPrice
      );

      link.append(
        title,
        priceContainer
      );

      /* Fin de modification MSRP - conserver les classes de prix du thème. */

      item.appendChild(link);
      list.appendChild(item);
    }

    group.append(
      heading,
      list
    );

    section.appendChild(group);

    return section;
  }

  showSearchStatus(message) {
    const status =
      document.createElement('div');

    status.className =
      'predictive-search-no-results';

    status.setAttribute(
      'role',
      'status'
    );

    status.textContent = message;

    this.predictiveSearchResults.replaceChildren(
      status
    );

    this.show();
  }

  /* Début modification MSRP - message compact pendant la recherche voiture globale. */
  showSearchLoading() {
    const status =
      document.createElement('div');

    status.className =
      'predictive-search-loading';

    status.setAttribute(
      'role',
      'status'
    );

    status.setAttribute(
      'aria-live',
      'polite'
    );

    status.setAttribute(
      'aria-atomic',
      'true'
    );

    status.textContent =
      'Recherche en cours…';

    /*
     * Styles volontairement locaux au message :
     * aucune modification CSS du thème et aucun impact
     * sur les moteurs des collections par marque.
     */
    status.style.width = 'fit-content';
    status.style.maxWidth = '100%';
    status.style.margin = '10px auto 0';
    status.style.padding = '7px 12px';
    status.style.boxSizing = 'border-box';
    status.style.background = 'rgba(255,255,255,.94)';
    status.style.color = '#17212e';
    status.style.borderRadius = '10px';
    status.style.fontSize = '14px';
    status.style.fontWeight = '700';
    status.style.lineHeight = '1.3';
    status.style.textAlign = 'center';
    status.style.boxShadow = '0 4px 14px rgba(0,0,0,.12)';

    this.predictiveSearchResults.replaceChildren(
      status
    );

    this.show();
  }
  /* Fin modification MSRP - message compact pendant la recherche voiture globale. */

  /* Fin de modification MSRP - recherche partielle dans la collection, sans fichier Liquid supplémentaire. */

  onChange() {
    const searchTerm =
      this.input.value.trim();

    /* Les résultats apparaissent à partir du deuxième caractère. */

    if (
      searchTerm.replace(/\s/g, '')
        .length < this.minimumSearchLength()
    ) {
      this.hide();
      return;
    }

    this.getSearchResults(
      searchTerm
    );
  }

  /* 🟨 ===== DÉBUT MODIFICATION MSRP : ANALYSE DU CODE ===== */

  getSearchFilterBrand() {
    const match =
      this.searchFilter.match(
        /product_type:\s*"([^"]+)"/i
      );

    return match
      ? match[1].trim()
      : '';
  }

  /* Début modification MSRP - contexte collection voiture globale. */

  getSearchFilterTag() {
    const match =
      this.searchFilter.match(
        /(?:^|\s)tag:(?:"([^"]+)"|([^\s]+))/i
      );

    return match
      ? (
          match[1] ||
          match[2] ||
          ''
        ).trim()
      : '';
  }

  isVehicleGlobalSearch() {
    return (
      this.searchMode ===
      'vehicle-global'
    );
  }

  /* Fin modification MSRP - contexte collection voiture globale. */

  normalizeText(value) {
    return value
      .normalize('NFD')
      .replace(
        /[\u0300-\u036f]/g,
        ''
      )
      .toLowerCase();
  }

  normalizeCode(value) {
    return this
      .normalizeText(value)
      .replace(
        /[^a-z0-9]/g,
        ''
      );
  }

  isCodeSearchTerm(value) {
    // Les débuts de codes et les codes complets
    // peuvent être alphabétiques.

    return /^[a-z0-9]+(?:\/[a-z0-9]+)?$/i.test(
      value.trim()
    );
  }

  matchesBrandWord(
    queryWord,
    brandWord
  ) {
    const normalize = (word) =>
      this
        .normalizeText(word)
        .replace(
          /^alpha$/,
          'alfa'
        );

    const query =
      normalize(queryWord);

    const brand =
      normalize(brandWord);

    if (query === brand) {
      return true;
    }

    if (
      !/^[a-z]+$/.test(query) ||
      !/^[a-z]+$/.test(brand)
    ) {
      return false;
    }

    if (
      query.length >= 2 &&
      brand.startsWith(query)
    ) {
      return true;
    }

    if (
      Math.min(
        query.length,
        brand.length
      ) < 4
    ) {
      return false;
    }

    if (
      Math.abs(
        query.length -
          brand.length
      ) > 1
    ) {
      return false;
    }

    if (
      query.length ===
      brand.length
    ) {
      const differences = [];

      for (
        let i = 0;
        i < query.length;
        i += 1
      ) {
        if (
          query[i] !== brand[i]
        ) {
          differences.push(i);
        }
      }

      if (
        differences.length === 1
      ) {
        return true;
      }

      if (
        differences.length !== 2
      ) {
        return false;
      }

      const [a, b] =
        differences;

      return (
        b === a + 1 &&
        query[a] === brand[b] &&
        query[b] === brand[a]
      );
    }

    const shorter =
      query.length <
      brand.length
        ? query
        : brand;

    const longer =
      query.length <
      brand.length
        ? brand
        : query;

    let index = 0;

    while (
      index < shorter.length &&
      shorter[index] ===
        longer[index]
    ) {
      index += 1;
    }

    return (
      shorter ===
      longer.slice(
        0,
        index
      ) +
        longer.slice(
          index + 1
        )
    );
  }

  prepareSearchTerm(
    searchTerm
  ) {
    if (!this.isMsrpSearch && !this.searchFilter) {
      return {
        term: searchTerm,
        searchedCode: ''
      };
    }

    if (this.getSearchFilterBrand().toLowerCase() === 'ral') {
      const code = searchTerm.normalize('NFKC').trim().replace(/^ral[\s-]*/i, '').trim();
      return {term:code, searchedCode:code};
    }

    let preparedTerm =
      this
        .normalizeText(
          searchTerm
        )
        .replace(
          /[-_]+/g,
          ' '
        )
        .replace(
          /\balpha\b/g,
          'alfa'
        )
        .replace(
          /\s*\/\s*/g,
          '/'
        )
        .trim();

    const brand =
      this.getSearchFilterBrand();

    if (brand) {
      const queryParts =
        preparedTerm.split(/\s+/);

      const brandParts =
        brand
          .replace(
            /[-_]+/g,
            ' '
          )
          .trim()
          .split(/\s+/);

      let matchedParts = 0;

      while (
        matchedParts <
          brandParts.length &&
        matchedParts <
          queryParts.length &&
        this.matchesBrandWord(
          queryParts[
            matchedParts
          ],
          brandParts[
            matchedParts
          ]
        )
      ) {
        matchedParts += 1;
      }

      if (
        matchedParts > 0
      ) {
        preparedTerm =
          queryParts.length >
          matchedParts
            ? queryParts
                .slice(
                  matchedParts
                )
                .join(' ')
            : brand;
      }
    }

    const codeParts =
      preparedTerm.split(/\s+/);

    if (
      /\d/.test(
        preparedTerm
      ) &&
      (
        codeParts.every(
          (part) =>
            /^[a-z0-9/]$/i.test(
              part
            )
        ) ||
        /^\d+\s+[a-z]$/i.test(
          preparedTerm
        )
      )
    ) {
      preparedTerm =
        codeParts.join('');
    }

    return {
      term: preparedTerm,

      searchedCode:
        this.isCodeSearchTerm(
          preparedTerm
        )
          ? this.normalizeCode(
              preparedTerm
            )
          : ''
    };
  }

  getProductTitle(product) {
    return (
      product
        .querySelector('p')
        ?.textContent.trim() ||
      ''
    );
  }

  getProductColorWords(product) {
    const title =
      this.getProductTitle(
        product
      );

    const separatorIndex =
      title.indexOf('&');

    const colorName =
      separatorIndex >= 0
        ? title.slice(
            separatorIndex + 1
          )
        : title;

    return (
      this
        .normalizeText(
          colorName
        )
        .match(
          /[a-z0-9]+/g
        ) ||
      []
    );
  }

  matchesTextWord(
    queryWord,
    titleWord
  ) {
    const query =
      this
        .normalizeText(
          queryWord
        )
        .replace(
          /[^a-z0-9]/g,
          ''
        );

    const title =
      this
        .normalizeText(
          titleWord
        )
        .replace(
          /[^a-z0-9]/g,
          ''
        );

    if (
      !query ||
      !title
    ) {
      return false;
    }

    if (
      title.includes(
        query
      )
    ) {
      return true;
    }

    if (
      query.length < 4 ||
      title.length <
        query.length ||
      !/^[a-z]+$/.test(
        query
      ) ||
      !/^[a-z]+$/.test(
        title
      )
    ) {
      return false;
    }

    return this.matchesBrandWord(
      query,
      title.slice(
        0,
        query.length
      )
    );
  }

  matchesProductText(
    product,
    searchTerm
  ) {
    const queryWords =
      this
        .normalizeText(
          searchTerm
        )
        .match(
          /[a-z0-9]+/g
        ) ||
      [];

    if (!queryWords.length) {
      return false;
    }

    const titleWords =
      this
        .normalizeText(
          this.getProductTitle(
            product
          )
        )
        .match(
          /[a-z0-9]+/g
        ) ||
      [];

    const colorWords =
      this.getProductColorWords(
        product
      );

    return queryWords.every(
      (queryWord) =>
        titleWords.some(
          (titleWord) =>
            titleWord.includes(
              queryWord
            )
        ) ||
        colorWords.some(
          (colorWord) =>
            this.matchesTextWord(
              queryWord,
              colorWord
            )
        )
    );
  }

  getProductCode(product) {
    const title =
      this.getProductTitle(
        product
      );

    const titlePrefix =
      title.split('&')[0] ||
      '';

    const normalizedPrefix =
      this.normalizeCode(
        titlePrefix
      );

    const normalizedBrand =
      this.normalizeCode(
        this.getSearchFilterBrand()
      );

    if (
      normalizedBrand &&
      normalizedPrefix.startsWith(
        normalizedBrand
      )
    ) {
      return normalizedPrefix.slice(
        normalizedBrand.length
      );
    }

    const lastWord =
      titlePrefix
        .trim()
        .split(/\s+/)
        .pop() ||
      '';

    return this.normalizeCode(
      lastWord
    );
  }

  codesMatch(
    productCode,
    searchedCode
  ) {
    return (
      productCode ===
      searchedCode
    );
  }

  filterCodeResults(
    resultsSection,
    searchedCode,
    allowOmittedLetter = false,
    searchTerm = ''
  ) {
    if (
      !this.isMsrpSearch ||
      !resultsSection
    ) {
      return;
    }

    const products =
      Array.from(
        resultsSection.querySelectorAll(
          '.predictive-search__item-product'
        )
      );

    const textTerm =
      (
        searchTerm ||
        searchedCode ||
        ''
      ).trim();

    const matches =
      products.filter(
        (product) => {
          const code =
            this.getProductCode(
              product
            );

          const matchesCode =
            searchedCode &&
            (
              code.startsWith(
                searchedCode
              ) ||
              (
                allowOmittedLetter &&
                /^[a-z]/.test(
                  code
                ) &&
                code
                  .slice(1)
                  .startsWith(
                    searchedCode
                  )
              )
            );

          const matchesText =
            !allowOmittedLetter &&
            textTerm &&
            this.matchesProductText(
              product,
              textTerm
            );

          return (
            matchesCode ||
            matchesText
          );
        }
      );

    const productsToKeep =
      new Set(matches);

    products.forEach(
      (product) => {
        if (
          !productsToKeep.has(
            product
          )
        ) {
          product.remove();
        }
      }
    );
  }

  /* Début modification MSRP - filtre réel du tag pour la collection voiture globale. */

  async filterResultsByTag(
    resultsSection,
    tagValue
  ) {
    if (
      !this.isVehicleGlobalSearch() ||
      !resultsSection ||
      !tagValue
    ) {
      return;
    }

    const products =
      Array.from(
        resultsSection.querySelectorAll(
          '.predictive-search__item-product'
        )
      );

    const normalizedTag =
      this.normalizeText(
        tagValue
      ).trim();

    await Promise.all(
      products.map(
        async (product) => {
          const link =
            product.querySelector(
              'a[href]'
            );

          if (!link) {
            product.remove();
            return;
          }

          const url =
            new URL(
              link.href,
              window.location.origin
            );

          const parts =
            url.pathname
              .split('/')
              .filter(Boolean);

          const productIndex =
            parts.indexOf(
              'products'
            );

          const handle =
            productIndex >= 0
              ? parts[
                  productIndex + 1
                ]
              : '';

          if (!handle) {
            product.remove();
            return;
          }

          try {
            const productData =
              await this.loadSearchProduct(
                handle
              );

            const tags =
              Array.isArray(
                productData.tags
              )
                ? productData.tags
                : String(
                    productData.tags ||
                    ''
                  )
                    .split(',')
                    .map(
                      (tag) =>
                        tag.trim()
                    )
                    .filter(Boolean);

            const hasTag =
              tags.some(
                (tag) =>
                  this
                    .normalizeText(tag)
                    .trim() ===
                  normalizedTag
              );

            if (!hasTag) {
              product.remove();
            }
          } catch (error) {
            product.remove();
          }
        }
      )
    );
  }

  /* Fin modification MSRP - filtre réel du tag pour la collection voiture globale. */

  /* 🟨 ===== FIN MODIFICATION MSRP ===== */

  /* 🟨 ===== DÉBUT AJOUT MSRP : TOLÉRANCE AUX CODES AVEC / ===== */

  getSlashAlternative(
    searchTerm
  ) {
    const parts =
      searchTerm
        .trim()
        .split(/\s+/);

    const lastPart =
      parts.pop();

    const match =
      lastPart.match(
        /^(\d+)([a-z])$/i
      );

    if (!match) {
      return '';
    }

    return [
      ...parts,
      `${match[1]}/${match[2]}`
    ].join(' ');
  }

  /* 🟨 ===== FIN AJOUT MSRP ===== */

  /* 🟨 ===== DÉBUT AJOUT MSRP : CONSTRUCTION DE LA REQUÊTE ===== */

  canSearchWithOmittedLetter(
    searchTerm
  ) {
    const lastPart =
      searchTerm
        .trim()
        .split(/\s+/)
        .pop() ||
      '';

    return (
      this.isMsrpSearch &&
      lastPart.length >= 2 &&
      lastPart.length <= 16 &&
      this.isCodeSearchTerm(
        lastPart
      )
    );
  }

  /* Début modification MSRP - alternatives réelles pour un code saisi sans sa première lettre. */

  getOmittedLetterSearchTerms(
    searchTerm
  ) {
    if (
      !this.isVehicleGlobalSearch() ||
      !this.canSearchWithOmittedLetter(
        searchTerm
      )
    ) {
      return [];
    }

    const parts =
      searchTerm
        .trim()
        .split(/\s+/);

    const code =
      parts.pop() || '';

    return Array.from(
      'abcdefghijklmnopqrstuvwxyz',
      (letter) =>
        [
          ...parts,
          `${letter}${code}`
        ].join(' ')
    );
  }

  /* Fin modification MSRP - alternatives réelles pour un code saisi sans sa première lettre. */

  /* Début modification MSRP - compléter un début de code dans la recherche voiture globale. */

  canSearchCodeContinuations(
    searchTerm
  ) {
    const code =
      this.normalizeCode(
        searchTerm
          .trim()
          .split(/\s+/)
          .pop() || ''
      );

    return (
      this.isVehicleGlobalSearch() &&
      code.length >= 2 &&
      code.length <= 16 &&
      /\d/.test(code) &&
      this.isCodeSearchTerm(
        searchTerm
          .trim()
          .split(/\s+/)
          .pop() || ''
      )
    );
  }

  getCodeContinuationSearchTerms(
    searchTerm
  ) {
    if (
      !this.canSearchCodeContinuations(
        searchTerm
      )
    ) {
      return [];
    }

    const parts =
      searchTerm
        .trim()
        .split(/\s+/);

    const code =
      parts.pop() || '';

    return Array.from(
      'abcdefghijklmnopqrstuvwxyz0123456789',
      (character) =>
        [
          ...parts,
          `${code}${character}`
        ].join(' ')
    );
  }

  /* Fin modification MSRP - compléter un début de code dans la recherche voiture globale. */

  buildSearchUrl(
    searchTerm,
    allowOmittedLetter = false
  ) {
    let apiSearchTerm =
      searchTerm;

    if (this.isMsrpSearch) {
      const terms =
        new Set([
          searchTerm
        ]);

      const slashAlternative =
        this.getSlashAlternative(
          searchTerm
        );

      if (slashAlternative) {
        terms.add(
          slashAlternative
        );
      }

      if (
        allowOmittedLetter &&
        this.canSearchWithOmittedLetter(
          searchTerm
        )
      ) {
        // Shopify ne gère pas *A7W*.
        // Une requête OR teste les lettres
        // initiales possibles.

        for (
          const term of
          Array.from(terms)
        ) {
          const parts =
            term
              .trim()
              .split(/\s+/);

          const code =
            parts.pop();

          for (
            const letter of
            'abcdefghijklmnopqrstuvwxyz'
          ) {
            terms.add(
              [
                ...parts,
                `${letter}${code}`
              ].join(' ')
            );
          }
        }
      }

      const queries =
        Array.from(
          terms,
          (term) =>
            term
              .split(/\s+/)
              .map(
                (part) => {
                  const token =
                    this
                      .normalizeText(
                        part
                      )
                      .replace(
                        /[^a-z0-9/]/g,
                        ''
                      );

                  return token
                    ? (
                        this.isVehicleGlobalSearch()
                          ? token
                          : `${token}*`
                      )
                    : '';
                }
              )
              .filter(Boolean)
              .join(' ')
        );

      apiSearchTerm =
        queries.length > 1
          ? `(${queries
              .map(
                (query) =>
                  `(${query})`
              )
              .join(' OR ')})`
          : queries[0];
    }

    /*
     * COLLECTION VOITURE GLOBALE :
     *
     * Liquid transmet :
     * tag:voiture
     *
     * Ce tag ne doit pas devenir un deuxième
     * mot de la requête prédictive, sinon Shopify
     * peut écarter des produits partageant le même
     * code constructeur entre plusieurs marques.
     *
     * On recherche donc le code dans le titre,
     * puis on vérifie réellement le tag après coup.
     *
     * Les collections par marque gardent leur
     * fonctionnement actuel avec product_type.
     */

    const tagValue =
      this.getSearchFilterTag();

    const isVehicleGlobal =
      this.isVehicleGlobalSearch();

    let finalSearchTerm =
      apiSearchTerm;

    if (this.searchFilter) {
      if (
        isVehicleGlobal &&
        tagValue
      ) {
        /*
         * Cas spécial collection voiture globale :
         * ne pas transformer "tag:voiture"
         * en mot-clé de recherche.
         */
        finalSearchTerm =
          apiSearchTerm;
      } else {
        /*
         * Tous les autres moteurs conservent
         * strictement le comportement précédent.
         */
        finalSearchTerm =
          tagValue
            ? `${apiSearchTerm} ${tagValue}`
            : `${apiSearchTerm} ${this.searchFilter}`;
      }
    }

    const resourceTypes =
      this.isMsrpSearch ||
      this.searchFilter
        ? 'product'
        : 'product,collection,page,article';

    const searchFields =
      isVehicleGlobal
        ? '&resources[options][fields]=title'
        : (
            tagValue
              ? '&resources[options][fields]=title,product_type,tag,variants.title,vendor'
              : ''
          );

    return (
      Theme.routes
        .predictive_search_url +
      `?q=${encodeURIComponent(
        finalSearchTerm
      )}` +
      '&section_id=predictive-search' +
      `&resources[type]=${resourceTypes}` +
      '&resources[limit]=10' +
      searchFields
    );
  }

  /* 🟨 ===== FIN AJOUT MSRP ===== */

  /* 🟨 ===== DÉBUT AJOUT MSRP : RÉCUPÉRATION DES RÉSULTATS ===== */

  fetchSearchResults(
    searchTerm,
    allowOmittedLetter = false,
    signal
  ) {
    return fetch(
      this.buildSearchUrl(
        searchTerm,
        allowOmittedLetter
      ),
      {
        signal
      }
    )
      .then(
        (response) => {
          if (
            !response.ok
          ) {
            throw new Error(
              response.status
            );
          }

          return response.text();
        }
      )
      .then(
        (text) => {
          const parsedDocument =
            new DOMParser()
              .parseFromString(
                text,
                'text/html'
              );

          return parsedDocument.querySelector(
            '#shopify-section-predictive-search'
          );
        }
      );
  }

  /* 🟨 ===== FIN AJOUT MSRP ===== */

  /* 🟨 ===== DÉBUT AJOUT MSRP : TRI DES PRODUITS DE A À Z ===== */

  /* Début modification MSRP - code exact prioritaire, puis tri alphabétique. */

  sortProductResults(
    resultsSection,
    searchedCode = ''
  ) {
    if (
      !this.isMsrpSearch ||
      !resultsSection
    ) {
      return;
    }

    const products =
      Array.from(
        resultsSection.querySelectorAll(
          '.predictive-search__item-product'
        )
      );

    if (
      products.length < this.minimumSearchLength()
    ) {
      return;
    }

    const groups =
      new Map();

    products.forEach(
      (product) => {
        const parent =
          product.parentElement;

        if (
          !groups.has(
            parent
          )
        ) {
          groups.set(
            parent,
            []
          );
        }

        groups
          .get(parent)
          .push(product);
      }
    );

    groups.forEach(
      (items, parent) => {
        items.sort(
          (a, b) => {
            if (
              searchedCode
            ) {
              const exactA =
                this.codesMatch(
                  this.getProductCode(
                    a
                  ),
                  searchedCode
                );

              const exactB =
                this.codesMatch(
                  this.getProductCode(
                    b
                  ),
                  searchedCode
                );

              if (
                exactA !== exactB
              ) {
                return exactA
                  ? -1
                  : 1;
              }
            }

            const titleA =
              a.textContent.trim();

            const titleB =
              b.textContent.trim();

            return titleA.localeCompare(
              titleB,
              'fr',
              {
                numeric: true,
                sensitivity:
                  'base'
              }
            );
          }
        );

        items.forEach(
          (item) => {
            parent.appendChild(
              item
            );
          }
        );
      }
    );
  }

  /* Fin de modification MSRP - code exact prioritaire, puis tri alphabétique. */

  /* 🟨 ===== FIN AJOUT MSRP ===== */

  /* Début modification MSRP - fusion sans doublons et limite de dix produits. */

  mergeProductResults(
    sections
  ) {
    const selector =
      '.predictive-search__item-product';

    const result =
      sections.find(
        (section) =>
          section?.querySelector(
            selector
          )
      ) ||
      sections.find(Boolean);

    if (!result) {
      return null;
    }

    const parent =
      result
        .querySelector(
          selector
        )
        ?.parentElement;

    if (!parent) {
      return result;
    }

    const seen =
      new Set();

    const products =
      sections.flatMap(
        (section) =>
          section
            ? Array.from(
                section.querySelectorAll(
                  selector
                )
              )
            : []
      );

    for (
      const product of
      products
    ) {
      const href =
        product
          .querySelector(
            'a[href]'
          )
          ?.getAttribute(
            'href'
          );

      const key =
        href
          ? new URL(
              href,
              window.location.href
            ).pathname
          : product
              .querySelector('p')
              ?.textContent.trim();

      if (
        seen.has(key)
      ) {
        product.remove();
        continue;
      }

      seen.add(key);

      parent.appendChild(
        product
      );
    }

    return result;
  }

  limitProductResults(
    resultsSection
  ) {
    if (
      !this.isMsrpSearch ||
      !resultsSection
    ) {
      return;
    }

    Array.from(
      resultsSection.querySelectorAll(
        '.predictive-search__item-product'
      )
    )
      .slice(10)
      .forEach(
        (product) =>
          product.remove()
      );
  }

  /* Fin de modification MSRP - fusion sans doublons et limite de dix produits. */

  showNoResults() {
    this.predictiveSearchResults.innerHTML = `
      <div
        class="predictive-search-no-results"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <div
          class="predictive-search-no-results__icon"
          aria-hidden="true"
        >
          !
        </div>

        <div class="predictive-search-no-results__content">
          <strong>
            Aucun code peinture trouvé
          </strong>

          <span>
            Vérifiez votre code ou essayez une autre référence dans notre catalogue.
            Si besoin, envoyez-nous une demande par mail :
            <a href="mailto:contact@msrp.fr">contact@msrp.fr</a>
          </span>
        </div>
      </div>
    `;

    this.show();
  }

  /* 🟨 ===== DÉBUT MODIFICATION MSRP : RECHERCHE ET AFFICHAGE ===== */

  async getSearchResults(
    searchTerm
  ) {
    if (this.isMsrpSearch || this.searchFilter) return this.getScopedSearchResults(searchTerm);

    const preparedSearch =
      this.prepareSearchTerm(
        searchTerm
      );

    const requestId =
      ++this.requestId;

    this.abortController?.abort();

    this.abortController =
      new AbortController();

    const {
      signal
    } =
      this.abortController;

    const isCurrent = () =>
      requestId ===
        this.requestId &&
      this.input.value.trim() ===
        searchTerm;

    try {

      /*
       * Collection voiture globale uniquement : afficher immédiatement
       * un retour visuel pendant les requêtes qui peuvent durer 1 à 2 secondes.
       * Les collections par marque conservent leur fonctionnement actuel.
       */
      if (
        this.isVehicleGlobalSearch()
      ) {
        this.showSearchLoading();
      }

      /* Début modification MSRP - résultats de la collection dès deux caractères. */

      const collectionPath =
        this.getCollectionSearchPath();

      if (collectionPath) {
        this.showSearchStatus(
          'Recherche en cours…'
        );

        const section =
          await this.getCollectionSearchResults(
            collectionPath,
            preparedSearch
          );

        if (!isCurrent()) {
          return;
        }

        if (
          !section.querySelector(
            '.predictive-search__item-product'
          )
        ) {
          this.showNoResults();
        } else {
          this.predictiveSearchResults.replaceChildren(
            section
          );

          this.show();
        }

        return;
      }

      /* Fin de modification MSRP - résultats de la collection dès deux caractères. */

      /* Début modification MSRP - compléter la recherche même si elle trouve déjà des produits. */

      const sections = [];
      let searchError = null;

      if (
        this.isVehicleGlobalSearch()
      ) {
        /*
         * COLLECTION VOITURE GLOBALE
         *
         * 1. Recherche normale du code saisi.
         * 2. Si aucun produit ne correspond, on teste réellement
         *    chaque lettre initiale possible avec des requêtes
         *    Predictive Search séparées.
         *
         * Exemple : A7W -> AA7W, BA7W, ... LA7W, ... ZA7W.
         * Cela évite la requête OR, que Predictive Search
         * n'interprète pas de manière fiable pour ce cas.
         */

        try {
          const primarySection =
            await this.fetchSearchResults(
              preparedSearch.term,
              false,
              signal
            );

          this.filterCodeResults(
            primarySection,
            preparedSearch.searchedCode,
            false,
            preparedSearch.term
          );

          await this.filterResultsByTag(
            primarySection,
            this.getSearchFilterTag()
          );

          sections.push(
            primarySection
          );
        } catch (error) {
          searchError = error;
        }

        let primaryProductCount =
          sections.reduce(
            (count, section) =>
              count +
              (
                section?.querySelectorAll(
                  '.predictive-search__item-product'
                ).length || 0
              ),
            0
          );

        /*
         * Si Shopify renvoie moins de dix produits pour un début de code
         * (ex. LA7), compléter avec les continuations possibles :
         * LA7A, LA7B, ... LA7W, ... LA70, etc.
         *
         * Cette étape ne s'exécute que dans la collection voiture globale,
         * à partir de trois caractères et seulement si la recherche primaire
         * a déjà trouvé au moins un produit. Elle ne touche donc pas aux
         * moteurs de recherche des collections par marque.
         */
        if (
          primaryProductCount > 0 &&
          primaryProductCount < 10 &&
          preparedSearch.searchedCode &&
          this.canSearchCodeContinuations(
            preparedSearch.term
          )
        ) {
          const continuationTerms =
            this.getCodeContinuationSearchTerms(
              preparedSearch.term
            );

          const continuationResponses =
            await Promise.allSettled(
              continuationTerms.map(
                (term) =>
                  this.fetchSearchResults(
                    term,
                    false,
                    signal
                  )
              )
            );

          if (!isCurrent()) {
            return;
          }

          for (
            const response of
            continuationResponses
          ) {
            if (
              response.status ===
              'rejected'
            ) {
              if (!searchError) {
                searchError =
                  response.reason;
              }

              continue;
            }

            const section =
              response.value;

            this.filterCodeResults(
              section,
              preparedSearch.searchedCode,
              false,
              preparedSearch.term
            );

            await this.filterResultsByTag(
              section,
              this.getSearchFilterTag()
            );

            sections.push(
              section
            );
          }

          primaryProductCount =
            sections.reduce(
              (count, section) =>
                count +
                (
                  section?.querySelectorAll(
                    '.predictive-search__item-product'
                  ).length || 0
                ),
              0
            );
        }

        const hasPrimaryProduct =
          primaryProductCount > 0;

        if (
          !hasPrimaryProduct &&
          preparedSearch.searchedCode &&
          this.canSearchWithOmittedLetter(
            preparedSearch.term
          )
        ) {
          const alternativeTerms =
            this.getOmittedLetterSearchTerms(
              preparedSearch.term
            );

          const alternativeResponses =
            await Promise.allSettled(
              alternativeTerms.map(
                (term) =>
                  this.fetchSearchResults(
                    term,
                    false,
                    signal
                  )
              )
            );

          if (!isCurrent()) {
            return;
          }

          for (
            const response of
            alternativeResponses
          ) {
            if (
              response.status ===
              'rejected'
            ) {
              if (!searchError) {
                searchError =
                  response.reason;
              }

              continue;
            }

            const section =
              response.value;

            this.filterCodeResults(
              section,
              preparedSearch.searchedCode,
              true,
              preparedSearch.term
            );

            await this.filterResultsByTag(
              section,
              this.getSearchFilterTag()
            );

            sections.push(
              section
            );
          }
        }
      } else {
        /*
         * AUTRES MOTEURS MSRP :
         * conserver strictement le fonctionnement existant.
         * Les collections par marque utilisent normalement
         * getCollectionSearchResults() plus haut et ne passent
         * donc pas dans cette branche.
         */

        const searchModes =
          [false];

        if (
          preparedSearch.searchedCode &&
          this.canSearchWithOmittedLetter(
            preparedSearch.term
          )
        ) {
          searchModes.push(true);
        }

        const responses =
          await Promise.allSettled(
            searchModes.map(
              (
                allowOmittedLetter
              ) =>
                this.fetchSearchResults(
                  preparedSearch.term,
                  allowOmittedLetter,
                  signal
                )
            )
          );

        if (!isCurrent()) {
          return;
        }

        for (
          let index = 0;
          index < responses.length;
          index += 1
        ) {
          const response =
            responses[index];

          if (
            response.status ===
            'rejected'
          ) {
            searchError =
              response.reason;

            continue;
          }

          const section =
            response.value;

          this.filterCodeResults(
            section,
            preparedSearch.searchedCode,
            searchModes[index],
            preparedSearch.term
          );

          sections.push(
            section
          );
        }
      }

      const resultsSection =
        this.isMsrpSearch
          ? this.mergeProductResults(
              sections
            )
          : sections[0];

      const hasAnyProduct =
        resultsSection?.querySelector(
          '.predictive-search__item-product'
        );

      if (
        searchError &&
        !hasAnyProduct
      ) {
        throw searchError;
      }

      /* Fin de modification MSRP - compléter la recherche même si elle trouve déjà des produits. */

      if (
        !resultsSection
      ) {
        this.hide();
        return;
      }

      const hasProducts =
        resultsSection.querySelector(
          '.predictive-search__item-product'
        );

      if (
        this.isMsrpSearch &&
        !hasProducts
      ) {
        this.showNoResults();
        return;
      }

      /* Début modification MSRP - trier puis afficher dix produits au maximum. */

      this.sortProductResults(
        resultsSection,
        preparedSearch.searchedCode
      );

      this.limitProductResults(
        resultsSection
      );

      /* Fin de modification MSRP - trier puis afficher dix produits au maximum. */

      this.predictiveSearchResults.innerHTML =
        resultsSection.innerHTML;

      this.show();

    } catch (error) {

      if (!isCurrent()) {
        return;
      }

      /* Début modification MSRP - distinguer une erreur réseau d'une absence de produit. */

      if (
        this.getCollectionSearchPath()
      ) {
        this.showSearchStatus(
          'La recherche est temporairement indisponible. Veuillez réessayer.'
        );
      } else {
        if (
          error.name ===
          'AbortError'
        ) {
          return;
        }

        this.hide();
      }

      /* Fin de modification MSRP - distinguer une erreur réseau d'une absence de produit. */

      console.warn(
        'MSRP : recherche temporairement indisponible.',
        error
      );
    }
  }

  /* 🟨 ===== FIN MODIFICATION MSRP ===== */

  show() {
    this.predictiveSearchResults.style.display =
      'block';
  }

  hide() {
    this.predictiveSearchResults.style.display =
      'none';
  }

  debounce(
    fn,
    wait
  ) {
    let timeout;

    return (...args) => {
      clearTimeout(
        timeout
      );

      timeout =
        setTimeout(
          () =>
            fn.apply(
              this,
              args
            ),
          wait
        );
    };
  }
}

if (
  !customElements.get(
    'predictive-search'
  )
) {
  customElements.define(
    'predictive-search',
    PredictiveSearch
  );
}
